import React, { useRef, useEffect, useCallback, useMemo, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  buildRoadRouteForStops,
  calculateBearing,
  fetchRoadRouteSegment,
  getTransportVisualConfig,
  hasValidLatLng,
  normalizeLatLng
} from './mapUtils';
import { Icon, OFFICE_LOCATION } from './LogisticsUtils';

const ROAD_ROUTE_DEBOUNCE_MS = 180;
const MAX_COMPLETED_STOPS_FOR_ROAD_DETAIL = 4;

const buildPointKey = (point) => {
  const normalized = normalizeLatLng(point);
  if (!normalized) return 'x';
  return `${normalized.lat.toFixed(5)},${normalized.lng.toFixed(5)}`;
};

const toSafeLatLng = (latlng) => L.latLng(Number(latlng.lat), Number(latlng.lng));

const calculateDistanceKm = (pointA, pointB) => {
  const normalizedA = normalizeLatLng(pointA);
  const normalizedB = normalizeLatLng(pointB);

  if (!normalizedA || !normalizedB) return null;

  return L.latLng(normalizedA.lat, normalizedA.lng).distanceTo(
    L.latLng(normalizedB.lat, normalizedB.lng)
  ) / 1000;
};

const toGeometryLatLngs = (geometry = []) =>
  (Array.isArray(geometry) ? geometry : [])
    .map((point) => normalizeLatLng(point))
    .filter(Boolean)
    .map((point) => [point.lat, point.lng]);

const mergeGeometryLatLngs = (segments = []) =>
  (Array.isArray(segments) ? segments : []).reduce((acc, segment) => {
    const geometry = toGeometryLatLngs(segment);
    if (!geometry.length) return acc;
    if (!acc.length) return [...geometry];

    const [firstPoint, ...restPoints] = geometry;
    const lastPoint = acc[acc.length - 1];

    if (lastPoint && firstPoint && lastPoint[0] === firstPoint[0] && lastPoint[1] === firstPoint[1]) {
      acc.push(...restPoints);
      return acc;
    }

    acc.push(...geometry);
    return acc;
  }, []);

const getPathDistanceMeters = (latLngs = []) => {
  if (!Array.isArray(latLngs) || latLngs.length < 2) return 0;

  let totalDistance = 0;
  for (let index = 1; index < latLngs.length; index += 1) {
    totalDistance += L.latLng(latLngs[index - 1][0], latLngs[index - 1][1]).distanceTo(
      L.latLng(latLngs[index][0], latLngs[index][1])
    );
  }

  return totalDistance;
};

const getPointAtPathProgress = (latLngs = [], ratio = 0) => {
  if (!Array.isArray(latLngs) || !latLngs.length) return null;
  if (latLngs.length === 1) {
    return {
      lat: latLngs[0][0],
      lng: latLngs[0][1],
      bearing: 0
    };
  }

  const clampedRatio = Math.max(0, Math.min(1, ratio));
  const totalDistance = getPathDistanceMeters(latLngs);
  if (!Number.isFinite(totalDistance) || totalDistance <= 0) {
    return {
      lat: latLngs[latLngs.length - 1][0],
      lng: latLngs[latLngs.length - 1][1],
      bearing: calculateBearing(latLngs[0][0], latLngs[0][1], latLngs[1][0], latLngs[1][1])
    };
  }

  const targetDistance = totalDistance * clampedRatio;
  let travelled = 0;

  for (let index = 1; index < latLngs.length; index += 1) {
    const segmentStart = latLngs[index - 1];
    const segmentEnd = latLngs[index];
    const segmentDistance = L.latLng(segmentStart[0], segmentStart[1]).distanceTo(
      L.latLng(segmentEnd[0], segmentEnd[1])
    );

    if (!Number.isFinite(segmentDistance) || segmentDistance <= 0) continue;

    if (travelled + segmentDistance >= targetDistance) {
      const segmentRatio = (targetDistance - travelled) / segmentDistance;
      const lat = segmentStart[0] + (segmentEnd[0] - segmentStart[0]) * segmentRatio;
      const lng = segmentStart[1] + (segmentEnd[1] - segmentStart[1]) * segmentRatio;

      return {
        lat,
        lng,
        bearing: calculateBearing(segmentStart[0], segmentStart[1], segmentEnd[0], segmentEnd[1])
      };
    }

    travelled += segmentDistance;
  }

  const lastSegmentStart = latLngs[latLngs.length - 2];
  const lastSegmentEnd = latLngs[latLngs.length - 1];
  return {
    lat: lastSegmentEnd[0],
    lng: lastSegmentEnd[1],
    bearing: calculateBearing(lastSegmentStart[0], lastSegmentStart[1], lastSegmentEnd[0], lastSegmentEnd[1])
  };
};

const normalizeCoordinate = (value, fallback = null) => {
  if (value === '' || value === null || value === undefined) return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeOfficeLocation = (officeLocation) => {
  const lat = normalizeCoordinate(officeLocation?.lat, OFFICE_LOCATION.lat);
  const lng = normalizeCoordinate(officeLocation?.lng, OFFICE_LOCATION.lng);
  const hasMeaningfulAddress = typeof officeLocation?.address === 'string' && officeLocation.address.trim().length > 0;
  const looksLikeNullIsland = Math.abs(lat || 0) < 0.001 && Math.abs(lng || 0) < 0.001;

  return {
    name: officeLocation?.name || OFFICE_LOCATION.name || 'Base operativa',
    address: officeLocation?.address || OFFICE_LOCATION.address || '',
    shortName: officeLocation?.shortName || '',
    lat: looksLikeNullIsland && !hasMeaningfulAddress ? OFFICE_LOCATION.lat : lat,
    lng: looksLikeNullIsland && !hasMeaningfulAddress ? OFFICE_LOCATION.lng : lng
  };
};

const hasRenderableOfficeLocation = (officeLocation) => {
  const normalizedOffice = normalizeLatLng(normalizeOfficeLocation(officeLocation));
  if (!normalizedOffice) return false;

  return !(Math.abs(normalizedOffice.lat) < 0.001 && Math.abs(normalizedOffice.lng) < 0.001);
};

const getInitialMapCenter = ({ officeLocation, liveRoutes = [], destinations = [], transporterLocation = null }) => {
  if (hasRenderableOfficeLocation(officeLocation)) {
    const normalizedOffice = normalizeOfficeLocation(officeLocation);
    return [normalizedOffice.lat, normalizedOffice.lng];
  }

  const fallbackPoint =
    liveRoutes.find((route) => hasValidLatLng(route?.currentLocation))?.currentLocation ||
    liveRoutes.flatMap((route) => route?.destinations || []).find((dest) => hasValidLatLng(dest?.latlng))?.latlng ||
    (destinations || []).find((dest) => hasValidLatLng(dest?.latlng))?.latlng ||
    (hasValidLatLng(transporterLocation) ? transporterLocation : null) ||
    OFFICE_LOCATION;

  return [Number(fallbackPoint.lat), Number(fallbackPoint.lng)];
};

const getOfficeBadgeLabel = (officeLocation) => {
  const baseLabel = String(officeLocation?.shortName || officeLocation?.name || 'Base')
    .trim()
    .replace(/[^A-Za-zÁÉÍÓÚÜÑáéíóúüñ0-9\s]/g, ' ');

  const words = baseLabel.split(/\s+/).filter(Boolean);

  if (!words.length) return 'BASE';
  if (words.length === 1) return words[0].slice(0, 3).toUpperCase();

  return words.slice(0, 2).map((word) => word[0]).join('').toUpperCase();
};

const createOfficeIcon = (officeLocation) => {
  const officeLabel = getOfficeBadgeLabel(officeLocation);

  return L.divIcon({
    html: `
      <div style="display:flex;flex-direction:column;align-items:center;gap:4px;">
        <div style="width:40px;height:40px;border-radius:999px;background:linear-gradient(180deg,#0f172a 0%,#1e293b 100%);color:white;border:2px solid white;box-shadow:0 12px 28px rgba(15,23,42,.28);display:flex;align-items:center;justify-content:center;font-weight:800;font-size:11px;letter-spacing:.08em;">${officeLabel}</div>
        <div style="width:6px;height:6px;border-radius:999px;background:#0f172a;opacity:.22;"></div>
      </div>`,
    className: 'bg-transparent border-0',
    iconSize: [42, 52],
    iconAnchor: [21, 42]
  });
};

const createDestinationIcon = (dest, isSelected = false, accentColor = null, subtle = false) => {
  const status = dest?.status || 'pending';
  const palette = {
    completed: '#22c55e',
    incomplete: '#f59e0b',
    cancelled: '#ef4444',
    en_camino: '#06b6d4',
    en_destino: '#0ea5e9',
    pending: '#64748b'
  };

  const color = accentColor || palette[status] || palette.pending;
  const ring = isSelected ? `0 0 0 6px ${color}26` : '0 0 0 0 transparent';
  const size = subtle ? 24 : 30;
  const fontSize = subtle ? 10 : 12;
  const lineHeight = subtle ? 8 : 12;
  const shadow = subtle ? `0 6px 16px ${color}22` : `0 8px 22px rgba(15,23,42,.28), ${ring}`;

  return L.divIcon({
    html: `
      <div style="display:flex;flex-direction:column;align-items:center;gap:4px;opacity:${subtle && !isSelected ? 0.9 : 1};transform:${isSelected ? 'scale(1.06)' : 'scale(1)'};transition:transform .2s ease;">
        <div style="width:${size}px;height:${size}px;border-radius:999px;background:${color};color:white;border:${subtle ? 1.5 : 2}px solid white;box-shadow:${shadow};display:flex;align-items:center;justify-content:center;font-size:${fontSize}px;font-weight:800;line-height:${lineHeight}px;">
          ${(Number(dest?.order) || 0) + 1}
        </div>
        ${isSelected ? '<div style="width:2px;height:12px;background:rgba(15,23,42,.18);"></div>' : ''}
      </div>
    `,
    className: 'bg-transparent border-0',
    iconSize: [size + 2, subtle ? 30 : 44],
    iconAnchor: [(size + 2) / 2, subtle ? 24 : 38]
  });
};

const createTransportIcon = (route, isSelected = false) => {
  const visual = getTransportVisualConfig(route?.transportKey || route?.transport || route?.transportType);
  const identityColor = route?.monitorAccentColor || visual.color;
  const identitySoftColor = route?.monitorAccentSoftColor || visual.softColor;
  const ringColor = isSelected ? 'rgba(14,165,233,0.22)' : 'rgba(255,255,255,0.35)';
  const statusColor = route?.statusColor || identityColor;

  return L.divIcon({
    html: `
      <div style="position:relative;display:flex;align-items:center;justify-content:center;">
        <div style="position:absolute;inset:-7px;border-radius:999px;background:${ringColor};filter:blur(0.2px);"></div>
        <div style="position:relative;width:42px;height:42px;border-radius:999px;background:linear-gradient(180deg, #ffffff 0%, ${identitySoftColor} 100%);border:3px solid ${identityColor};box-shadow:0 12px 24px rgba(15,23,42,.28);display:flex;align-items:center;justify-content:center;">
          <div style="position:absolute;right:-2px;bottom:-2px;width:12px;height:12px;border-radius:999px;background:${statusColor};border:2px solid white;"></div>
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="${identityColor}" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="${visual.path}" />
          </svg>
        </div>
      </div>
    `,
    className: 'bg-transparent border-0',
    iconSize: [42, 42],
    iconAnchor: [21, 21]
  });
};

const getMultiRouteOperationPoints = (routes = []) =>
  routes.flatMap((route) => {
    const points = [];

    if (hasValidLatLng(route?.currentLocation)) {
      points.push(normalizeLatLng(route.currentLocation));
    }

    (Array.isArray(route?.transporterPath) ? route.transporterPath : []).forEach((point) => {
      const normalized = normalizeLatLng(point);
      if (normalized) points.push(normalized);
    });

    (Array.isArray(route?.destinations) ? route.destinations : []).forEach((dest) => {
      const normalized = normalizeLatLng(dest?.latlng);
      if (normalized) points.push(normalized);
    });

    return points;
  });

const shouldIncludeOfficeInMultiBounds = (routes = [], officeLocation) => {
  const normalizedOffice = hasRenderableOfficeLocation(officeLocation) ? normalizeLatLng(normalizeOfficeLocation(officeLocation)) : null;
  if (!normalizedOffice) return false;

  const operationPoints = getMultiRouteOperationPoints(routes);
  if (!operationPoints.length) return true;

  const centroid = operationPoints.reduce(
    (acc, point) => ({ lat: acc.lat + point.lat, lng: acc.lng + point.lng }),
    { lat: 0, lng: 0 }
  );

  centroid.lat /= operationPoints.length;
  centroid.lng /= operationPoints.length;

  const spreadKm = operationPoints.reduce((maxDistance, point) => {
    const distanceKm = calculateDistanceKm(point, centroid) || 0;
    return Math.max(maxDistance, distanceKm);
  }, 0);

  const officeDistanceKm = calculateDistanceKm(normalizedOffice, centroid) || 0;
  const dynamicThresholdKm = Math.max(40, spreadKm * 3 + 25);

  return officeDistanceKm <= dynamicThresholdKm;
};

const buildMultiRouteBounds = (routes = [], officeLocation, { includeOffice = true } = {}) => {
  const bounds = L.latLngBounds();

  const normalizedOffice = hasRenderableOfficeLocation(officeLocation) ? normalizeOfficeLocation(officeLocation) : null;

  if (includeOffice && normalizedOffice) {
    bounds.extend([Number(normalizedOffice.lat), Number(normalizedOffice.lng)]);
  }

  routes.forEach((route) => {
    if (hasValidLatLng(route?.currentLocation)) {
      bounds.extend([Number(route.currentLocation.lat), Number(route.currentLocation.lng)]);
    }

    const pathPoints = Array.isArray(route?.transporterPath) ? route.transporterPath : [];
    pathPoints.forEach((point) => {
      if (hasValidLatLng(point)) {
        bounds.extend([Number(point.lat), Number(point.lng)]);
      }
    });

    const destinations = Array.isArray(route?.destinations) ? route.destinations : [];
    destinations.forEach((dest) => {
      if (hasValidLatLng(dest?.latlng)) {
        bounds.extend([Number(dest.latlng.lat), Number(dest.latlng.lng)]);
      }
    });
  });

  return bounds;
};



const extendBoundsWithPoint = (bounds, point) => {
  const normalized = normalizeLatLng(point);
  if (normalized) {
    bounds.extend([normalized.lat, normalized.lng]);
  }
};

const buildFocusedRouteBounds = (route, officeLocation) => {
  const bounds = L.latLngBounds();
  if (!route) return bounds;

  const currentLocation = normalizeLatLng(route?.currentLocation);
  const nextDestination = normalizeLatLng(route?.nextDestination?.latlng || route?.activeLeg?.to?.latlng);
  const activeFrom = normalizeLatLng(route?.activeLeg?.from?.latlng);
  const transporterPath = Array.isArray(route?.transporterPath) ? route.transporterPath : [];
  const recentTrail = transporterPath
    .slice(Math.max(transporterPath.length - 4, 0))
    .map((point) => normalizeLatLng(point))
    .filter(Boolean);

  if (currentLocation) {
    bounds.extend([currentLocation.lat, currentLocation.lng]);
  }

  if (nextDestination) {
    bounds.extend([nextDestination.lat, nextDestination.lng]);
  }

  if (!nextDestination && activeFrom) {
    bounds.extend([activeFrom.lat, activeFrom.lng]);
  }

  recentTrail.forEach((point) => bounds.extend([point.lat, point.lng]));

  if (!bounds.isValid()) {
    extendBoundsWithPoint(bounds, officeLocation);
    extendBoundsWithPoint(bounds, route?.currentLocation);

    const fallbackDestinations = Array.isArray(route?.destinations) ? route.destinations.slice(0, 3) : [];
    fallbackDestinations.forEach((dest) => extendBoundsWithPoint(bounds, dest?.latlng));
  }

  return bounds;
};

const buildSelectedRouteBounds = (route, officeLocation, { includeOffice = true } = {}) => {
  const bounds = L.latLngBounds();

  const normalizedOffice = hasRenderableOfficeLocation(officeLocation) ? normalizeOfficeLocation(officeLocation) : null;

  if (includeOffice && normalizedOffice) {
    bounds.extend([Number(normalizedOffice.lat), Number(normalizedOffice.lng)]);
  }

  if (!route) return bounds;

  if (hasValidLatLng(route?.currentLocation)) {
    bounds.extend([Number(route.currentLocation.lat), Number(route.currentLocation.lng)]);
  }

  const pathPoints = Array.isArray(route?.transporterPath) ? route.transporterPath : [];
  pathPoints.forEach((point) => {
    if (hasValidLatLng(point)) {
      bounds.extend([Number(point.lat), Number(point.lng)]);
    }
  });

  const destinations = Array.isArray(route?.destinations) ? route.destinations : [];
  destinations.forEach((dest) => {
    if (hasValidLatLng(dest?.latlng)) {
      bounds.extend([Number(dest.latlng.lat), Number(dest.latlng.lng)]);
    }
  });

  return bounds;
};

const MapComponent = ({
  destinations,
  officeLocation = OFFICE_LOCATION,
  transporterLocation = null,
  transporterPath = [],
  routeRoadMetrics = null,
  activeLeg = null,
  liveRoutes = [],
  selectedRouteId = null,
  focusedRouteId = null,
  focusedDestination = null,
  onRouteSelect = null
}) => {
  const safeOfficeLocation = useMemo(() => normalizeOfficeLocation(officeLocation), [officeLocation]);
  const officeIsRenderable = useMemo(() => hasRenderableOfficeLocation(officeLocation), [officeLocation]);

  const mapContainerRef = useRef(null);
  const mapInstance = useRef(null);
  const markersLayer = useRef(null);
  const completedPathLayer = useRef(null);
  const activeLegLayer = useRef(null);
  const truckMarker = useRef(null);
  const animationFrameId = useRef(null);
  const initialFitDone = useRef(false);
  const previousFocusedRouteId = useRef(null);
  const resizeObserverRef = useRef(null);
  const [showReferencesPanel, setShowReferencesPanel] = useState(false);
  const [roadGeometryState, setRoadGeometryState] = useState({
    singleActiveLeg: null,
    singleCompletedRoute: null,
    multiActiveLegs: {}
  });

  const multiTransportMode = Array.isArray(liveRoutes) && liveRoutes.length > 0;
  const includeOfficeInMultiView = multiTransportMode
    ? officeIsRenderable && shouldIncludeOfficeInMultiBounds(liveRoutes, safeOfficeLocation)
    : officeIsRenderable;

  const singleRouteRoadSignature = useMemo(() => {
    if (multiTransportMode) return 'multi-disabled';

    const completedSignature = (Array.isArray(destinations) ? destinations : [])
      .filter((dest) => dest?.status === 'completed' && hasValidLatLng(dest?.latlng))
      .sort(
        (a, b) =>
          (a.completedAt?.toDate?.()?.getTime?.() || 0) -
          (b.completedAt?.toDate?.()?.getTime?.() || 0)
      )
      .slice(0, MAX_COMPLETED_STOPS_FOR_ROAD_DETAIL - 1)
      .map((dest) => `${dest.id}:${buildPointKey(dest.latlng)}`)
      .join('|');

    return [
      buildPointKey(officeIsRenderable ? safeOfficeLocation : null),
      buildPointKey(transporterLocation),
      buildPointKey(activeLeg?.from?.latlng),
      buildPointKey(activeLeg?.to?.latlng),
      completedSignature
    ].join('::');
  }, [multiTransportMode, destinations, activeLeg, transporterLocation, safeOfficeLocation, officeIsRenderable]);

  const multiRouteRoadSignature = useMemo(() => {
    if (!multiTransportMode) return 'single-disabled';

    return liveRoutes
      .map((route) => [
        route?.id || 'route',
        buildPointKey(route?.currentLocation),
        buildPointKey(route?.activeLeg?.from?.latlng),
        buildPointKey(route?.activeLeg?.to?.latlng || route?.nextDestination?.latlng)
      ].join(':'))
      .join('|');
  }, [multiTransportMode, liveRoutes]);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    let timeoutId = null;

    const loadRoadGeometry = async () => {
      if (multiTransportMode) {
        const entries = await Promise.all(
          liveRoutes.map(async (route) => {
            const activeTo = normalizeLatLng(route?.activeLeg?.to?.latlng || route?.nextDestination?.latlng);
            const startCandidate =
              normalizeLatLng(route?.currentLocation) ||
              normalizeLatLng(route?.activeLeg?.from?.latlng) ||
              (includeOfficeInMultiView ? normalizeLatLng(safeOfficeLocation) : null);

            if (!startCandidate || !activeTo) {
              return [route.id, null];
            }

            try {
              const segment = await fetchRoadRouteSegment(startCandidate, activeTo, { signal: controller.signal });
              return [route.id, segment];
            } catch (error) {
              if (error?.name === 'AbortError') throw error;
              return [route.id, null];
            }
          })
        );

        if (!cancelled) {
          setRoadGeometryState({
            singleActiveLeg: null,
            singleCompletedRoute: null,
            multiActiveLegs: Object.fromEntries(entries.filter(([routeId, segment]) => routeId && segment))
          });
        }

        return;
      }

      const completedStops = [
        officeIsRenderable ? normalizeLatLng(safeOfficeLocation) : null,
        ...(Array.isArray(destinations)
          ? destinations
              .filter((dest) => dest?.status === 'completed' && hasValidLatLng(dest?.latlng))
              .sort(
                (a, b) =>
                  (a.completedAt?.toDate?.()?.getTime?.() || 0) -
                  (b.completedAt?.toDate?.()?.getTime?.() || 0)
              )
              .slice(0, MAX_COMPLETED_STOPS_FOR_ROAD_DETAIL - 1)
              .map((dest) => normalizeLatLng(dest.latlng))
          : [])
      ].filter(Boolean);

      const activeTo = normalizeLatLng(activeLeg?.to?.latlng);
      const activeFrom =
        normalizeLatLng(transporterLocation) ||
        normalizeLatLng(activeLeg?.from?.latlng) ||
        (officeIsRenderable ? normalizeLatLng(safeOfficeLocation) : null);

      const [singleCompletedRoute, singleActiveLeg] = await Promise.all([
        completedStops.length > 1 && completedStops.length <= MAX_COMPLETED_STOPS_FOR_ROAD_DETAIL
          ? buildRoadRouteForStops(completedStops, { signal: controller.signal }).catch((error) => {
              if (error?.name === 'AbortError') throw error;
              return null;
            })
          : Promise.resolve(null),
        activeFrom && activeTo
          ? fetchRoadRouteSegment(activeFrom, activeTo, { signal: controller.signal }).catch((error) => {
              if (error?.name === 'AbortError') throw error;
              return null;
            })
          : Promise.resolve(null)
      ]);

      if (!cancelled) {
        setRoadGeometryState({
          singleActiveLeg,
          singleCompletedRoute,
          multiActiveLegs: {}
        });
      }
    };

    const scheduleTimeout = typeof window !== 'undefined' && window.setTimeout ? window.setTimeout.bind(window) : setTimeout;
    const clearScheduledTimeout = typeof window !== 'undefined' && window.clearTimeout ? window.clearTimeout.bind(window) : clearTimeout;

    timeoutId = scheduleTimeout(() => {
      loadRoadGeometry().catch((error) => {
        if (error?.name === 'AbortError') return;
        if (!cancelled) {
          setRoadGeometryState({
            singleActiveLeg: null,
            singleCompletedRoute: null,
            multiActiveLegs: {}
          });
        }
      });
    }, ROAD_ROUTE_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      controller.abort();
      if (timeoutId) {
        clearScheduledTimeout(timeoutId);
      }
    };
  }, [
    multiTransportMode,
    includeOfficeInMultiView,
    officeIsRenderable,
    safeOfficeLocation,
    activeLeg,
    transporterLocation,
    destinations,
    liveRoutes,
    singleRouteRoadSignature,
    multiRouteRoadSignature
  ]);

  const recenterMap = useCallback(() => {
    if (!mapInstance.current) return;

    const focusedLiveRoute = multiTransportMode
      ? liveRoutes.find((route) => route.id === focusedRouteId) || null
      : null;

    const bounds = multiTransportMode
      ? (focusedLiveRoute
          ? buildFocusedRouteBounds(focusedLiveRoute, safeOfficeLocation)
          : buildMultiRouteBounds(liveRoutes, safeOfficeLocation, { includeOffice: includeOfficeInMultiView }))
      : (() => {
          const nextBounds = L.latLngBounds();
          if (officeIsRenderable) {
            nextBounds.extend([safeOfficeLocation.lat, safeOfficeLocation.lng]);
          }

          (destinations || []).forEach((dest) => {
            if (hasValidLatLng(dest?.latlng)) {
              nextBounds.extend([Number(dest.latlng.lat), Number(dest.latlng.lng)]);
            }
          });

          if (hasValidLatLng(transporterLocation)) {
            nextBounds.extend([Number(transporterLocation.lat), Number(transporterLocation.lng)]);
          }

          return nextBounds;
        })();

    if (bounds.isValid()) {
      mapInstance.current.fitBounds(bounds, {
        padding: multiTransportMode ? [70, 70] : [50, 50],
        maxZoom: multiTransportMode ? 16 : 15
      });
    }
  }, [multiTransportMode, liveRoutes, safeOfficeLocation, officeIsRenderable, destinations, transporterLocation, focusedRouteId, includeOfficeInMultiView]);

  useEffect(() => {
    if (mapContainerRef.current && !mapInstance.current) {
      mapInstance.current = L.map(mapContainerRef.current, {
        center: getInitialMapCenter({ officeLocation: safeOfficeLocation, liveRoutes, destinations, transporterLocation }),
        zoom: 12,
        zoomControl: false,
        scrollWheelZoom: false
      });

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors'
      }).addTo(mapInstance.current);

      L.control.zoom({ position: 'bottomright' }).addTo(mapInstance.current);

      completedPathLayer.current = L.layerGroup().addTo(mapInstance.current);
      activeLegLayer.current = L.layerGroup().addTo(mapInstance.current);
      markersLayer.current = L.layerGroup().addTo(mapInstance.current);
    }

    if (mapContainerRef.current && typeof ResizeObserver !== 'undefined') {
      resizeObserverRef.current = new ResizeObserver(() => {
        if (!mapInstance.current) return;

        requestAnimationFrame(() => {
          if (mapInstance.current) {
            mapInstance.current.invalidateSize(false);
          }
        });
      });

      resizeObserverRef.current.observe(mapContainerRef.current);
    }

    return () => {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
        animationFrameId.current = null;
      }

      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }

      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (
      !mapInstance.current ||
      !markersLayer.current ||
      !completedPathLayer.current ||
      !activeLegLayer.current
    ) {
      return;
    }

    markersLayer.current.clearLayers();
    completedPathLayer.current.clearLayers();
    activeLegLayer.current.clearLayers();

    if (animationFrameId.current) {
      cancelAnimationFrame(animationFrameId.current);
      animationFrameId.current = null;
    }

    if (truckMarker.current) {
      truckMarker.current.remove();
      truckMarker.current = null;
    }

    const bounds = L.latLngBounds();

    const shouldRenderOfficeMarker = officeIsRenderable && (!multiTransportMode || includeOfficeInMultiView);

    if (shouldRenderOfficeMarker) {
      L.marker([safeOfficeLocation.lat, safeOfficeLocation.lng], { icon: createOfficeIcon(safeOfficeLocation) })
        .addTo(markersLayer.current)
        .bindPopup(`<b>${safeOfficeLocation.name || 'Base operativa'}</b><br>${safeOfficeLocation.address || 'Oficina / base operativa'}`);

      bounds.extend([safeOfficeLocation.lat, safeOfficeLocation.lng]);
    }

    if (multiTransportMode) {
      const selectedRoute =
        liveRoutes.find((route) => route.id === selectedRouteId) || liveRoutes[0] || null;
      const focusedRoute = focusedRouteId
        ? liveRoutes.find((route) => route.id === focusedRouteId) || null
        : null;

      liveRoutes.forEach((route) => {
        const isSelected = !!selectedRoute && route.id === selectedRoute.id;
        const isFocused = !!focusedRoute && route.id === focusedRoute.id;
        const visual = getTransportVisualConfig(route?.transportKey || route?.transport || route?.transportType);
        const pathColor = route?.monitorAccentColor || visual.color;
        const validPathLatLngs = (Array.isArray(route?.transporterPath) ? route.transporterPath : [])
          .map((point) => normalizeLatLng(point))
          .filter(Boolean)
          .map((point) => [point.lat, point.lng]);

        if (validPathLatLngs.length > 1) {
          L.polyline(validPathLatLngs, {
            color: pathColor,
            weight: isFocused ? 6 : isSelected ? 4.5 : 3,
            opacity: isFocused ? 0.95 : isSelected ? 0.78 : 0.5
          }).addTo(completedPathLayer.current);

          validPathLatLngs.forEach((point) => bounds.extend(point));
        }

        const routeUsesVisibleOffice = includeOfficeInMultiView && officeIsRenderable && hasValidLatLng(route?.activeLeg?.from?.latlng) && calculateDistanceKm(route?.activeLeg?.from?.latlng, safeOfficeLocation) !== null && (calculateDistanceKm(route?.activeLeg?.from?.latlng, safeOfficeLocation) || 0) < 1.5;
        const activeTo = normalizeLatLng(route?.activeLeg?.to?.latlng);
        const activeFrom =
          normalizeLatLng(route?.currentLocation) ||
          normalizeLatLng(route?.activeLeg?.from?.latlng) ||
          (routeUsesVisibleOffice ? normalizeLatLng(safeOfficeLocation) : null);

        if (activeFrom && activeTo) {
          const roadSegment = roadGeometryState.multiActiveLegs?.[route.id] || null;
          const activeGeometry = toGeometryLatLngs(roadSegment?.geometry);
          const activeLine = activeGeometry.length > 1
            ? activeGeometry
            : [
                [activeFrom.lat, activeFrom.lng],
                [activeTo.lat, activeTo.lng]
              ];

          L.polyline(activeLine, {
            color: pathColor,
            weight: isFocused ? 6.5 : isSelected ? 5 : 4,
            opacity: isFocused ? 0.98 : isSelected ? 0.84 : 0.58,
            dashArray: isFocused ? '12, 8' : isSelected ? '11, 9' : '10, 12'
          }).addTo(activeLegLayer.current);

          activeLine.forEach((point) => bounds.extend(point));
        }

        const routeDestinations = Array.isArray(route?.destinations) ? route.destinations : [];
        routeDestinations.forEach((dest) => {
          if (!hasValidLatLng(dest?.latlng)) return;

          const latlng = [Number(dest.latlng.lat), Number(dest.latlng.lng)];
          const destinationIsFocused = !!focusedDestination && focusedDestination.routeId === route.id && (!focusedDestination.destinationId || focusedDestination.destinationId === dest.id);
          const emphasizeDestination = isFocused || isSelected || destinationIsFocused;

          bounds.extend(latlng);

          L.marker(latlng, { icon: createDestinationIcon(dest, emphasizeDestination, pathColor, !emphasizeDestination) })
            .addTo(markersLayer.current)
            .bindPopup(
              `<b>${(Number(dest.order) || 0) + 1}. ${dest.name || 'Destino'}</b><br>${dest.address || ''}<br><small>${route.transporterName || route.name || 'Ruta activa'}</small>`
            );
        });

        const currentPoint = normalizeLatLng(route?.currentLocation);
        if (currentPoint) {
          const marker = L.marker([currentPoint.lat, currentPoint.lng], {
            icon: createTransportIcon(route, isSelected || isFocused)
          }).addTo(markersLayer.current);

          const popupHtml = `
            <div style="min-width:220px;font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif;">
              <div style="font-weight:800;font-size:14px;color:#0f172a;">${route.name || 'Ruta activa'}</div>
              <div style="margin-top:2px;font-size:12px;color:#334155;">${route.transporterName || 'Transporte sin nombre'} · ${visual.label}</div>
              <div style="margin-top:8px;display:flex;gap:6px;flex-wrap:wrap;">
                <span style="padding:2px 8px;border-radius:999px;background:${route.statusSoftColor || visual.softColor};color:${route.statusColor || pathColor};font-size:11px;font-weight:700;">${route.liveStatusLabel || 'Activo'}</span>
                <span style="padding:2px 8px;border-radius:999px;background:#eff6ff;color:#1d4ed8;font-size:11px;font-weight:700;">ETA ${route.etaLabel || 'N/D'}</span>
              </div>
              <div style="margin-top:8px;font-size:12px;color:#475569;">
                <div><strong>Próxima parada:</strong> ${route.nextDestination?.name || 'Sin definir'}</div>
                <div><strong>Actualización:</strong> ${route.lastUpdateLabel || 'Sin datos'}</div>
              </div>
            </div>
          `;

          marker.bindPopup(popupHtml);
          marker.on('click', () => {
            if (typeof onRouteSelect === 'function') {
              onRouteSelect(route.id);
            }
          });

          bounds.extend([currentPoint.lat, currentPoint.lng]);
        }

      });

      const focusChanged = previousFocusedRouteId.current !== focusedRouteId;
      const focusedBounds = focusedRoute
        ? buildFocusedRouteBounds(focusedRoute, safeOfficeLocation)
        : null;
      const allRoutesBounds = buildMultiRouteBounds(liveRoutes, safeOfficeLocation, { includeOffice: includeOfficeInMultiView });

      if (focusChanged && focusedBounds && focusedBounds.isValid()) {
        mapInstance.current.fitBounds(focusedBounds, {
          padding: [80, 80],
          maxZoom: 16
        });
      } else if (focusChanged && !focusedRoute && allRoutesBounds.isValid()) {
        mapInstance.current.fitBounds(allRoutesBounds, {
          padding: [70, 70],
          maxZoom: 14
        });
      } else if (bounds.isValid() && !initialFitDone.current) {
        mapInstance.current.fitBounds(bounds, { padding: [60, 60], maxZoom: 14 });
        initialFitDone.current = true;
      }

      previousFocusedRouteId.current = focusedRouteId;
      return;
    }

    const validDestinations = (destinations || []).filter((dest) => hasValidLatLng(dest?.latlng));

    validDestinations.forEach((dest) => {
      const latlng = [Number(dest.latlng.lat), Number(dest.latlng.lng)];
      bounds.extend(latlng);

      const isCompleted = dest.status === 'completed';
      const isEnCamino = dest.status === 'en_camino';

      const typeConfig = {
        Entrega: '#2563eb',
        Retiro: '#16a34a',
        Transporte: '#7c3aed',
        'Entrega y Retiro': '#f59e0b'
      };

      let color = typeConfig[dest.type] || '#64748b';
      if (isCompleted) color = '#22c55e';
      if (isEnCamino) color = '#06b6d4';

      const customIcon = L.divIcon({
        html: `
          <div style="display:flex;flex-direction:column;align-items:center;">
            <div style="width:32px;height:32px;border-radius:999px;background:${color};color:white;border:2px solid white;box-shadow:0 8px 18px rgba(15,23,42,.28);display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:800;">
              ${(dest.order ?? 0) + 1}
            </div>
          </div>
        `,
        className: 'bg-transparent border-0',
        iconSize: [32, 40],
        iconAnchor: [16, 34]
      });

      L.marker(latlng, { icon: customIcon })
        .addTo(markersLayer.current)
        .bindPopup(`<b>${(dest.order ?? 0) + 1}. ${dest.name || 'Destino'}</b><br>${dest.address || ''}`);
    });

    const persistedFullRouteLine = toGeometryLatLngs(routeRoadMetrics?.geometry);
    if (persistedFullRouteLine.length > 1) {
      L.polyline(persistedFullRouteLine, {
        color: '#94a3b8',
        weight: 3,
        opacity: 0.45
      }).addTo(completedPathLayer.current);

      persistedFullRouteLine.forEach((point) => bounds.extend(point));
    }

    const completedDestinationIds = validDestinations
      .filter((d) => d.status === 'completed')
      .sort(
        (a, b) =>
          (a.completedAt?.toDate?.()?.getTime?.() || 0) -
          (b.completedAt?.toDate?.()?.getTime?.() || 0)
      )
      .map((d) => d.id)
      .filter(Boolean);

    const completedPoints = [
      ...(officeIsRenderable ? [[safeOfficeLocation.lat, safeOfficeLocation.lng]] : []),
      ...validDestinations
        .filter((d) => d.status === 'completed')
        .sort(
          (a, b) =>
            (a.completedAt?.toDate?.()?.getTime?.() || 0) -
            (b.completedAt?.toDate?.()?.getTime?.() || 0)
        )
        .map((d) => [Number(d.latlng.lat), Number(d.latlng.lng)])
    ];

    const persistedCompletedLine = mergeGeometryLatLngs(
      completedDestinationIds.map((destinationId) => routeRoadMetrics?.legsByDestinationId?.[destinationId]?.geometry)
    );
    const completedGeometry = toGeometryLatLngs(roadGeometryState.singleCompletedRoute?.geometry);
    const completedLine = completedGeometry.length > 1
      ? completedGeometry
      : persistedCompletedLine.length > 1
        ? persistedCompletedLine
        : completedPoints;

    if (completedLine.length > 1) {
      L.polyline(completedLine, {
        color: '#22c55e',
        weight: 4,
        opacity: 0.7,
        dashArray: '5, 10'
      }).addTo(completedPathLayer.current);

      completedLine.forEach((point) => bounds.extend(point));
    }

    const activeToValid = hasValidLatLng(activeLeg?.to?.latlng);
    const activeFromValid = hasValidLatLng(activeLeg?.from?.latlng);

    if (activeToValid) {
      const startPoint = activeFromValid
        ? toSafeLatLng(activeLeg.from.latlng)
        : L.latLng(safeOfficeLocation.lat, safeOfficeLocation.lng);
      const endPoint = toSafeLatLng(activeLeg.to.latlng);
      const activeGeometry = toGeometryLatLngs(roadGeometryState.singleActiveLeg?.geometry);
      const persistedActiveGeometry = toGeometryLatLngs(routeRoadMetrics?.legsByDestinationId?.[activeLeg?.to?.id]?.geometry);
      const activeLine = activeGeometry.length > 1
        ? activeGeometry
        : persistedActiveGeometry.length > 1
          ? persistedActiveGeometry
          : [[startPoint.lat, startPoint.lng], [endPoint.lat, endPoint.lng]];

      L.polyline(activeLine, {
        color: '#0891b2',
        weight: 5,
        opacity: 0.8,
        dashArray: '10, 10'
      }).addTo(activeLegLayer.current);

      activeLine.forEach((point) => bounds.extend(point));

      const initialPoint = getPointAtPathProgress(activeLine, 0) || {
        lat: startPoint.lat,
        lng: startPoint.lng,
        bearing: 0
      };

      const truckIcon = L.divIcon({
        html: `<div style="transform: rotate(${Number.isFinite(initialPoint.bearing) ? initialPoint.bearing : 0}deg);" class="transition-transform duration-1000">
          <svg viewBox="0 0 40 40" class="w-10 h-10 drop-shadow-lg">
            <path d="M35.5,25.5 H32.5 V22.5 H28.25 C28.25,22.5 28.25,22.5 28.25,22.5 28.25,20.25 26.5,18.5 24.25,18.5 H12.5 V10.25 C12.5,10.25 12.5,10.25 12.5,10.25 12.5,8 14.25,6.25 16.5,6.25 H29 C30.25,6.25 31.25,7.25 31.25,8.5 V15 H35.5 C36,15 36.25,15.5 36.25,16 V24 C36.25,24.75 36,25.5 35.5,25.5 z" fill="#FFFFFF" stroke="#4B5563" stroke-width="1.5"></path>
            <path d="M10,31.25 C8.25,31.25 6.75,29.75 6.75,28 C6.75,26.25 8.25,24.75 10,24.75 C11.75,24.75 13.25,26.25 13.25,28 C13.25,29.75 11.75,31.25 10,31.25 z" fill="#FFFFFF" stroke="#4B5563" stroke-width="1.5"></path>
            <path d="M28,31.25 C26.25,31.25 24.75,29.75 24.75,28 C24.75,26.25 26.25,24.75 28,24.75 C29.75,24.75 31.25,26.25 31.25,28 C31.25,29.75 29.75,31.25 28,31.25 z" fill="#FFFFFF" stroke="#4B5563" stroke-width="1.5"></path>
            <path d="M12.5,18.5 H5 C4.25,18.5 3.75,19.25 3.75,20 V28 C3.75,28.75 4.25,29.5 5,29.5 H6.75" fill="none" stroke="#4B5563" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"></path>
            <path d="M31.25,28 H32.5 C33.25,28 33.75,27.5 33.75,26.75 V25.5" fill="none" stroke="#4B5563" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"></path>
            <path d="M13.25,28 H24.75" fill="none" stroke="#4B5563" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"></path>
          </svg>
        </div>`,
        className: 'bg-transparent border-0',
        iconSize: [40, 40],
        iconAnchor: [20, 20]
      });

      truckMarker.current = L.marker([initialPoint.lat, initialPoint.lng], { icon: truckIcon }).addTo(activeLegLayer.current);

      const distance = getPathDistanceMeters(activeLine);
      if (!Number.isFinite(distance) || distance <= 0) {
        truckMarker.current.setLatLng(endPoint);
      } else {
        const averageSpeedKmh = 80;
        const speedMps = (averageSpeedKmh * 1000) / 3600;
        const duration = (distance / speedMps) * 1000;

        if (Number.isFinite(duration) && duration > 0) {
          let startTime = null;

          const animate = (timestamp) => {
            if (!startTime) startTime = timestamp;

            const progress = timestamp - startTime;
            const ratio = Math.min(progress / duration, 1);
            const nextPoint = getPointAtPathProgress(activeLine, ratio);

            if (nextPoint && truckMarker.current) {
              truckMarker.current.setLatLng(L.latLng(nextPoint.lat, nextPoint.lng));
            }

            if (ratio < 1) {
              animationFrameId.current = requestAnimationFrame(animate);
            }
          };

          animationFrameId.current = requestAnimationFrame(animate);
        } else {
          truckMarker.current.setLatLng(endPoint);
        }
      }
    }

    if (hasValidLatLng(transporterLocation)) {
      const transporterIconHtml = `
        <div class="flex items-center justify-center bg-green-600 text-white w-8 h-8 rounded-full border-2 border-white shadow-lg">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25" />
          </svg>
        </div>
      `;

      const customTransporterIcon = L.divIcon({
        html: transporterIconHtml,
        className: 'bg-transparent border-0',
        iconSize: [32, 32],
        iconAnchor: [16, 32]
      });

      L.marker([Number(transporterLocation.lat), Number(transporterLocation.lng)], { icon: customTransporterIcon })
        .addTo(markersLayer.current)
        .bindPopup('<b>Transportista</b><br>Ubicación actual');

      bounds.extend([Number(transporterLocation.lat), Number(transporterLocation.lng)]);
    }

    if (transporterPath && transporterPath.length > 1) {
      const pathLatLngs = transporterPath
        .filter((p) => hasValidLatLng(p))
        .map((p) => [Number(p.lat), Number(p.lng)]);

      if (pathLatLngs.length > 1) {
        L.polyline(pathLatLngs, {
          color: '#94a3b8',
          weight: 2,
          opacity: 0.35,
          dashArray: '4, 8'
        }).addTo(markersLayer.current);
      }
    }

    if (bounds.isValid() && !initialFitDone.current) {
      mapInstance.current.fitBounds(bounds, { padding: [50, 50], maxZoom: 16 });
      initialFitDone.current = true;
    }
  }, [
    activeLeg,
    destinations,
    liveRoutes,
    multiTransportMode,
    safeOfficeLocation,
    officeIsRenderable,
    onRouteSelect,
    includeOfficeInMultiView,
    focusedRouteId,
    focusedDestination,
    selectedRouteId,
    transporterLocation,
    transporterPath,
    routeRoadMetrics,
    roadGeometryState
  ]);


  useEffect(() => {
    if (!mapInstance.current) return;

    const rafId = requestAnimationFrame(() => {
      if (mapInstance.current) {
        mapInstance.current.invalidateSize(false);
      }
    });

    return () => cancelAnimationFrame(rafId);
  }, [
    focusedRouteId,
    focusedDestination?.destinationId,
    selectedRouteId,
    multiTransportMode,
    liveRoutes?.length,
    destinations?.length,
    transporterPath?.length
  ]);

  useEffect(() => {
    if (!mapInstance.current || !multiTransportMode || !focusedDestination) return;

    const normalizedPoint = normalizeLatLng(focusedDestination?.latlng);
    if (!normalizedPoint) return;

    const relatedRoute = liveRoutes.find((route) => route.id === focusedDestination.routeId) || null;
    const currentLocation = normalizeLatLng(relatedRoute?.currentLocation);

    if (currentLocation) {
      const bounds = L.latLngBounds();
      bounds.extend([currentLocation.lat, currentLocation.lng]);
      bounds.extend([normalizedPoint.lat, normalizedPoint.lng]);
      mapInstance.current.fitBounds(bounds, { padding: [70, 70], maxZoom: 16 });
      return;
    }

    mapInstance.current.setView([normalizedPoint.lat, normalizedPoint.lng], Math.max(mapInstance.current.getZoom(), 16), {
      animate: true
    });
  }, [focusedDestination, liveRoutes, multiTransportMode]);

  const showOfficeOutsideNotice = multiTransportMode && officeIsRenderable && !includeOfficeInMultiView;

  return (
    <div className="relative isolate w-full h-full min-h-0 overflow-hidden rounded-2xl">
      <style>{`
        .leaflet-container {
          background: linear-gradient(180deg, #f8fafc 0%, #e2e8f0 100%);
        }

        .leaflet-pane,
        .leaflet-control,
        .leaflet-top,
        .leaflet-bottom {
          z-index: 1 !important;
        }

        .leaflet-tooltip-pane {
          z-index: 2 !important;
        }

        .leaflet-popup-pane {
          z-index: 3 !important;
        }
      `}</style>

      <div ref={mapContainerRef} className="w-full h-full rounded-2xl shadow-inner z-0 border border-slate-200" />

      {showOfficeOutsideNotice ? (
        <div className="absolute left-4 top-4 max-w-[20rem] rounded-2xl border border-amber-200 bg-white/95 backdrop-blur px-3 py-2 shadow-lg z-10">
          <p className="text-[10px] font-black uppercase tracking-[0.18em] text-amber-600">Vista operativa local</p>
          <p className="mt-1 text-xs text-slate-600">La base quedó fuera del encuadre general para priorizar destinos y unidades activas.</p>
        </div>
      ) : null}

      {multiTransportMode && liveRoutes.length > 0 ? (
        <>
          {showReferencesPanel ? (
            <div className="absolute left-4 bottom-20 z-[12] w-[min(24rem,calc(100%-2rem))] rounded-2xl border border-slate-200 bg-white/95 backdrop-blur px-3 py-3 shadow-xl">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">Referencias activas</p>
                  <p className="mt-1 text-xs text-slate-500">Tocá una unidad para marcarla en el mapa.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowReferencesPanel(false)}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 hover:bg-slate-50"
                  aria-label="Cerrar referencias"
                  title="Cerrar referencias"
                >
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="mt-3 flex flex-wrap gap-2 max-h-48 overflow-y-auto pr-1">
                {liveRoutes.map((route) => {
                  const accentColor = route?.monitorAccentColor || getTransportVisualConfig(route?.transportKey || route?.transport || route?.transportType).color;
                  const isSelected = route?.id === selectedRouteId;
                  const isFocused = route?.id === focusedRouteId;

                  return (
                    <button
                      key={route.id}
                      type="button"
                      onClick={() => {
                        if (typeof onRouteSelect === 'function') {
                          onRouteSelect(route.id);
                        }
                      }}
                      className={`inline-flex items-center gap-2 rounded-full border px-2.5 py-1.5 text-[11px] font-black transition-colors ${isSelected ? 'text-white border-transparent shadow-sm' : 'text-slate-700 border-slate-200 hover:bg-slate-50 bg-white'}`}
                      style={isSelected ? { backgroundColor: accentColor } : undefined}
                      title={route?.transporterName || route?.name || 'Unidad activa'}
                    >
                      <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: accentColor }} />
                      <span className="truncate max-w-[132px]">{route?.transporterName || route?.name || 'Unidad activa'}</span>
                      {isFocused ? <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${isSelected ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'}`}>zoom</span> : null}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}

          <button
            type="button"
            onClick={() => setShowReferencesPanel((current) => !current)}
            className="absolute left-4 bottom-4 z-[12] inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/95 backdrop-blur px-3 py-2 text-slate-700 shadow-lg hover:bg-slate-50"
            aria-label={showReferencesPanel ? 'Ocultar referencias activas' : 'Mostrar referencias activas'}
            title={showReferencesPanel ? 'Ocultar referencias activas' : 'Mostrar referencias activas'}
          >
            <span className="inline-flex h-2.5 w-2.5 rounded-full bg-slate-900" />
            <span className="text-[11px] font-black uppercase tracking-[0.14em]">Referencias</span>
            <span className="inline-flex items-center justify-center rounded-full bg-slate-900 px-2 py-0.5 text-[10px] font-black text-white">{liveRoutes.length}</span>
          </button>
        </>
      ) : null}

      <button
        onClick={recenterMap}
        className="absolute top-4 right-4 bg-white/95 backdrop-blur text-slate-700 p-2 rounded-full shadow-lg hover:bg-slate-100 z-[12] border border-slate-200"
        title="Recentrar Mapa"
      >
        <Icon path="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" className="w-5 h-5" />
        <Icon path="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" className="w-5 h-5" />
      </button>
    </div>
  );
};

export default MapComponent;
